/**
 * AffiliatesSection Component
 *
 * Displays organizational affiliations with logos
 */

import React from 'react'
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardMedia,
} from '@mui/material'
import Grid from '@mui/material/Grid2'

export interface Affiliate {
  id: string
  name: string
  logo: string
  logoAlt: string
  website?: string
}

export interface AffiliatesSectionProps {
  /**
   * Section title
   */
  title?: string

  /**
   * List of affiliates to display
   */
  affiliates: Affiliate[]

  /**
   * Optional CSS class name for styling
   */
  className?: string

  /**
   * Test ID for automated testing
   */
  testId?: string
}

/**
 * AffiliatesSection Component
 */
export const AffiliatesSection: React.FC<AffiliatesSectionProps> = ({
  title = 'Proud Affiliates',
  affiliates,
  className,
  testId = 'affiliates-section',
}) => {
  return (
    <Box
      component="section"
      aria-labelledby="affiliates-title"
      className={className}
      data-testid={testId}
      sx={{
        py: { xs: 6, md: 8 },
        bgcolor: 'grey.50',
      }}
    >
      <Container maxWidth="lg">
        {/* Section Title */}
        <Typography
          id="affiliates-title"
          variant="h2"
          component="h2"
          align="center"
          gutterBottom
          sx={{
            mb: 6,
            fontWeight: 700,
            fontSize: { xs: '2rem', md: '2.5rem' },
          }}
        >
          {title}
        </Typography>

        {/* Affiliates Grid */}
        <Grid container spacing={4} justifyContent="center">
          {affiliates.map(affiliate => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={affiliate.id}>
              <Card
                component={affiliate.website ? 'a' : 'div'}
                href={affiliate.website}
                target={affiliate.website ? '_blank' : undefined}
                rel={affiliate.website ? 'noopener noreferrer' : undefined}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  textDecoration: 'none',
                  transition:
                    'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                  '&:hover': affiliate.website
                    ? {
                        transform: 'translateY(-4px)',
                        boxShadow: 4,
                      }
                    : {},
                }}
              >
                <CardMedia
                  component="img"
                  image={affiliate.logo}
                  alt={affiliate.logoAlt}
                  sx={{
                    height: 180,
                    objectFit: 'contain',
                    p: 3,
                    bgcolor: 'common.white',
                  }}
                />
                <CardContent
                  sx={{
                    flexGrow: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    py: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    component="h3"
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.95rem',
                      lineHeight: 1.4,
                    }}
                  >
                    {affiliate.name}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  )
}

export default AffiliatesSection
